import { hasPassThroughDate, passesDateThrough } from './dates'
import { type AnyZod, DEFAULT_WRAPPERS, innerOf, inspect, shapeEntries, TRANSPARENT_WRAPPERS } from './introspect'
import { labelOf } from './labels'

// What one declared field needs, worked out ONCE and reused for every invocation.
//
// The interesting part is `absence`. An author writes `.optional()` or `.nullable()` — whichever reads
// naturally — and should not have to predict which absent form a client will send. So instead of reading the
// wrapper chain, we ASK THE SCHEMA what it accepts: two `safeParse` probes settle it, and they are immune to
// wrapper order (`.optional().nullable()` vs `.nullable().optional()`), to `.refine()` wrapping the type, and
// to any modifier zod adds later. Introspection would have to keep up with all three.

export type AbsencePolicy =
  // The schema tolerates both forms, so whichever arrived is passed through (`.nullish()`).
  | 'as-received'
  // Only `undefined` is accepted (`.optional()`, `.default(v)`), so any absence becomes that.
  | 'undefined'
  // Only `null` is accepted (`.nullable()`), so any absence becomes that.
  | 'null'
  // Neither — the field has nothing to fall back on.
  | 'required'

export interface FieldPlan {
  name: string
  schema: AnyZod
  // The author's display name, used in messages. Falls back to the key.
  label: string
  // Core zod type past the wrappers: 'string' | 'number' | 'boolean' | 'enum' | 'date' | …
  // For a union this is the FIRST branch's type — see `isUnion`.
  coreType: string
  /**
   * True when the declaration is a `z.union()`.
   *
   * Carried because `coreType` alone stops describing the field: a union resolves to its first branch, so a
   * `z.union([z.email(), z.literal('')])` reports `'string'` and would then treat a blank as a VALUE. It is
   * not one — `''` has always been absence on a union, which is exactly what makes the documented
   * "blank means not set" spelling (`z.union([z.url(), z.literal('')]).optional()`) work.
   */
  isUnion: boolean
  absence: AbsencePolicy
  // True when a declared `.default(v)` will absorb an absent value — which is also what lets a blank string
  // fall back to the default rather than surviving as `''`.
  absorbsBlank: boolean
  /**
   * True for a `z.date()` whose value must reach the handler UNCHANGED — validated through a converted copy,
   * handed over as the host sent it. False for `z.coerce.date()`, which the author asked to convert.
   *
   * Set when the field ITSELF is such a date; `restoresDates` covers one nested anywhere inside it.
   */
  passThroughDate: boolean
  // Whether anything at any depth needs that treatment, so the restore walk can be skipped entirely.
  restoresDates: boolean
}

// Ask the schema which absent forms it accepts. A probe can only throw for an async refinement placed
// outside the modifier, in which case treating the field as required is the safe reading.
/**
 * The absent forms the author DECLARED, read off the wrapper chain.
 *
 * Used only where the probe below cannot be trusted — see `probeAbsence`. It has to agree with the probe on
 * every schema the probe handles, which is why it treats `.default()` as accepting `undefined`: zod applies
 * a default to `undefined`, so that is what the probe observes too.
 */
function declaredAbsence(schema: AnyZod): { absence: AbsencePolicy; absorbsBlank: boolean } {
  let current: AnyZod | undefined = schema
  let acceptsUndefined = false
  let acceptsNull = false
  let absorbsBlank = false
  // `.nonoptional()` re-asserts a requirement over whatever it wraps — an `.optional()` further in does not
  // undo it, so nothing after it may set a flag. Mirrors the same rule in ./introspect.ts `inspect`.
  let sealed = false

  while (current?._zod) {
    const def = current._zod.def ?? {}
    const type = def.type as string

    if (type === 'nonoptional') {
      sealed = true
    } else if (TRANSPARENT_WRAPPERS.has(type)) {
      // Says nothing about absence — look straight through it, exactly as `inspect` does.
      current = innerOf(def, type)
      continue
    } else if (sealed) {
      break
    } else if (type === 'optional') {
      acceptsUndefined = true
    } else if (type === 'nullable') {
      acceptsNull = true
    } else if (type === 'nullish') {
      acceptsUndefined = true
      acceptsNull = true
    } else if (DEFAULT_WRAPPERS.has(type)) {
      acceptsUndefined = true
      absorbsBlank = true
    } else {
      break
    }

    current = def.innerType as AnyZod
  }

  const absence: AbsencePolicy = acceptsUndefined && acceptsNull ? 'as-received'
    : acceptsUndefined ? 'undefined'
      : acceptsNull ? 'null'
        : 'required'

  return { absence, absorbsBlank }
}

// `z.coerce.*` converts an ABSENT value into a junk one rather than rejecting it — `new Date(null)` is the
// epoch, `Number('')` is 0, `String(undefined)` is the text `'undefined'`, `Boolean(null)` is false. So a
// coercing schema answers "yes" to the probe below for a field the author declared REQUIRED, and a blank
// silently becomes 1970-01-01 or 0 instead of being reported. Where coercion is what makes the answer yes,
// the declaration is the authority instead.
function coerces(schema: AnyZod): boolean {
  return inspect(schema).core?._zod?.def?.coerce === true
}

/**
 * `z.json()` — the same problem as `z.coerce.*` above, from the other direction.
 *
 * It declares `z.unknown()` underneath, because what reaches the handler is a parsed JSON value and `null`
 * is one of those. The probe then sees a schema that accepts `null` and reads it as "null is how this field
 * spells absence", so a bare `z.json()` published as OPTIONAL — a field the author never marked optional and
 * which the runtime does in fact reject when it is missing.
 *
 * The probe cannot tell a legal value from an absent form by asking, so the declaration is the authority
 * here too: `.nonoptional()` on the helper means required, and an author's own `.optional()` sits outside it
 * and still wins.
 */
const parsesJson = (schema: AnyZod): boolean => !!inspect(schema).meta.frJson

function probeAbsence(schema: AnyZod): { absence: AbsencePolicy; absorbsBlank: boolean } {
  if (coerces(schema) || parsesJson(schema)) {
    return declaredAbsence(schema)
  }

  const parse = (value: unknown): { success: boolean; data?: unknown } => {
    try {
      return (schema as unknown as { safeParse: (v: unknown) => { success: boolean; data?: unknown } }).safeParse(value)
    } catch {
      return { success: false }
    }
  }

  const asUndefined = parse(undefined)
  const asNull = parse(null)

  // "Accepts both" must win over "has a default": for `z.string().nullable().default('x')` the author said
  // null is meaningful, and zod applies the default to `undefined` on its own.
  const absence: AbsencePolicy = asUndefined.success && asNull.success ? 'as-received'
    : asUndefined.success ? 'undefined'
      : asNull.success ? 'null'
        : 'required'

  // A default reveals itself by yielding something other than what was fed in. This is also why a falsy
  // default (`''`, `0`, `false`) is detected correctly, where a truthiness check would drop it.
  return { absence, absorbsBlank: asUndefined.success && asUndefined.data !== undefined }
}

// Probing costs two `safeParse` calls, and the deep walk asks the same element or property schema once per
// item. Schemas are stable objects built at registration, so the answer is cached against the schema itself.
const probeCache = new WeakMap<object, { absence: AbsencePolicy; absorbsBlank: boolean }>()

/** The absent forms a schema accepts, at any depth. Same question `planField` asks for a top-level field. */
export function absenceOf(schema: AnyZod | undefined): { absence: AbsencePolicy; absorbsBlank: boolean } {
  if (!schema || typeof schema !== 'object') {
    return { absence: 'as-received', absorbsBlank: false }
  }

  const cached = probeCache.get(schema as object)

  if (cached) {
    return cached
  }

  const probed = probeAbsence(schema)

  probeCache.set(schema as object, probed)

  return probed
}

export function planField(name: string, schema: AnyZod): FieldPlan {
  const ins = inspect(schema)
  const { absence, absorbsBlank } = probeAbsence(schema)

  return {
    name,
    schema,
    label: labelOf(name, schema),
    coreType: ins.coreType,
    isUnion : !!ins.branches,
    absence,
    absorbsBlank,
    passThroughDate: passesDateThrough(ins),
    restoresDates  : hasPassThroughDate(schema, inspect, shapeEntries),
  }
}
